import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const CompareContext = createContext(null);
const STORAGE_KEY = 'lovinahome-compare-ids';
const MAX_ITEMS = 3;

export const CompareProvider = ({ children }) => {
  const [ids, setIds] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
      /* ignore quota errors */
    }
  }, [ids]);

  const add = useCallback((id) => {
    if (!id) return;
    setIds((prev) => {
      if (prev.includes(id)) return prev;
      if (prev.length >= MAX_ITEMS) return prev; // at cap
      return [...prev, id];
    });
  }, []);

  const remove = useCallback((id) => {
    setIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const toggle = useCallback((id) => {
    if (!id) return false;
    let added = false;
    setIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_ITEMS) return prev;
      added = true;
      return [...prev, id];
    });
    return added;
  }, []);

  const clear = useCallback(() => setIds([]), []);

  const value = useMemo(
    () => ({ ids, count: ids.length, max: MAX_ITEMS, add, remove, toggle, clear, has: (id) => ids.includes(id) }),
    [ids, add, remove, toggle, clear]
  );

  return <CompareContext.Provider value={value}>{children}</CompareContext.Provider>;
};

export const useCompare = () => {
  const ctx = useContext(CompareContext);
  if (!ctx) throw new Error('useCompare must be used inside CompareProvider');
  return ctx;
};
